import { Skeleton } from '@vizit/atoms';
import { Text, TextColor, TextSize } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { getAriaLabel, getLegendLabels } from './utils';

enum ImageLegendVariant {
  Default = 'default',
  AttentionMap = 'attention-map',
  GazeSequence = 'gaze-sequence',
  FocalPoints = 'focal-points',
  AttentionDrivers = 'attention-drivers',
  AttentionDetractors = 'attention-detractors',
}

interface ImageLegendProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The variant of the image legend
   * @default 'default'
   */
  variant?: ImageLegendVariant;
  /**
   * The left label text
   */
  leftLabel?: string;
  /**
   * The right label text
   */
  rightLabel?: string;
  /**
   * Show numbers for gaze sequence (only applies to gaze-sequence variant)
   */
  gazeSequencePoints?: number;
  /**
   * Whether the component is in loading state
   * @default false
   */
  isLoading?: boolean;
}

const containerClasses = cva({
  base: 'w-[360px] h-20 border border-primary flex items-center justify-between relative bg-primary text-xs text-secondary px-8',
  variants: {
    variant: {
      default: 'flex items-center justify-center p-8 ',
      'attention-map': '',
      'gaze-sequence': '',
      'focal-points': '',
      'attention-drivers': 'justify-center ',
      'attention-detractors': 'justify-center',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const gazeSequenceNumberClasses = cva({
  base: 'w-10 h-10 rounded-full border border-brand-primary flex items-center justify-center bg-primary text-brand-tertiary',
});

const stepClasses = cva({
  base: 'h-4',
  variants: {
    detractor: {
      true: 'w-[17px]',
    },
    driver: {
      true: 'w-11',
    },
  },
});

function ImageLegend(props: ImageLegendProps) {
  const {
    variant = ImageLegendVariant.Default,
    leftLabel: leftLabelProp,
    rightLabel: rightLabelProp,
    gazeSequencePoints = 4,
    isLoading = false,
    className,
    ...other
  } = props;

  const gazePointIds = React.useMemo(() => {
    return Array.from({ length: gazeSequencePoints }).map(
      (_, i) => `gaze-point-${props.id || Math.random().toString(36).substr(2, 9)}-${i}`,
    );
  }, [gazeSequencePoints, props.id]);

  const { leftLabel: defaultLeftLabel, rightLabel: defaultRightLabel } = React.useMemo(() => {
    return getLegendLabels(variant);
  }, [variant]);

  const leftLabel = leftLabelProp ?? defaultLeftLabel;
  const rightLabel = rightLabelProp ?? defaultRightLabel;

  const ariaLabel = React.useMemo(() => {
    return getAriaLabel(variant, leftLabel, rightLabel, gazeSequencePoints);
  }, [variant, leftLabel, rightLabel, gazeSequencePoints]);

  if (isLoading) {
    return <Skeleton className="w-[360px] h-20" aria-label="Loading image legend" />;
  }

  return (
    <figure className={cx(containerClasses({ variant }), className)} aria-label={ariaLabel} {...other}>
      {variant === ImageLegendVariant.AttentionMap && (
        <>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[100px]">
            {leftLabel || 'Less attention'}
          </Text>
          <div
            className="w-[139px] h-4 bg-gradient-to-r from-purple-500 via-green-300 via-orange-300 to-error-600"
            style={{ background: 'linear-gradient(90deg, #7A5AF8 0%, #73E2A3 35.28%, #FF9C66 68.73%, #D92D20 100%)' }}
            aria-hidden="true"
          />
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[100px] ">
            {rightLabel || 'More attention'}
          </Text>
        </>
      )}
      {variant === ImageLegendVariant.GazeSequence && (
        <>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[130px]">
            {leftLabel || 'Gaze Sequence points'}
          </Text>
          <ul className="flex items-center relative" aria-label={`${gazeSequencePoints} gaze sequence points`}>
            {gazePointIds.map((id, index) => (
              <React.Fragment key={id}>
                {index > 0 && <div className="h-px w-18 bg-brand-solid" aria-hidden="true" />}
                <li className={gazeSequenceNumberClasses()} aria-label={`Gaze point ${index + 1}`}>
                  {index + 1}
                </li>
              </React.Fragment>
            ))}
          </ul>
        </>
      )}
      {variant === ImageLegendVariant.FocalPoints && (
        <>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[100px]">
            {leftLabel || 'Focal Points'}
          </Text>
          <div className="flex items-center gap-4" role="img" aria-label="Focal points scale from 0 to 100%">
            <span>0</span>
            <div className="w-92 h-px bg-brand-solid" aria-hidden="true" />
            <span>100%</span>
          </div>
        </>
      )}
      {variant === ImageLegendVariant.AttentionDrivers && (
        <div className="flex items-center gap-6">
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[85px]">
            {leftLabel || 'Low driver'}
          </Text>
          <div className="flex items-center h-16" role="img" aria-label="Attention drivers intensity scale">
            <div className={cx(stepClasses({ driver: true }), 'bg-green-50')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-100')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-200')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-300')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-400')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-500')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-600')} aria-hidden="true" />
            <div className={cx(stepClasses({ driver: true }), 'bg-green-700')} aria-hidden="true" />
          </div>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[85px]">
            {rightLabel || 'High driver'}
          </Text>
        </div>
      )}
      {variant === ImageLegendVariant.AttentionDetractors && (
        <div className="flex items-center gap-6">
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[85px]">
            {leftLabel || 'Low detractor'}
          </Text>
          <div className="flex items-center h-16" role="img" aria-label="Attention detractors intensity scale">
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-50')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-100')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-200')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-300')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-400')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-500')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-600')} aria-hidden="true" />
            <div className={cx(stepClasses({ detractor: true }), 'bg-error-700')} aria-hidden="true" />
          </div>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[85px]">
            {rightLabel || 'High detractor'}
          </Text>
        </div>
      )}
      {variant === ImageLegendVariant.Default && (
        <>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[150px]">
            {leftLabel}
          </Text>
          <Text size={TextSize.Xs} color={TextColor.Secondary} className="max-w-[150px]">
            {rightLabel}
          </Text>
        </>
      )}
    </figure>
  );
}

export { ImageLegend, ImageLegendVariant };
export type { ImageLegendProps };
